"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncStoreCatalogueResponse = exports.SyncVarusCatalogueResponse = exports.VarusCatalogueRecord = exports.StoreCatalogueCardPrice = exports.StoreCataloguePwlBarcode = exports.StoreCatalogueBackTableObject = exports.StoreCatalogueProgrammeTables = exports.StoreCatalogueProgrammeDataset = exports.STORE_CATALOGUE_CARD_PROGRAMMES_QUERY = exports.VarusCatalogueBarcodeReplaceRule = exports.STORE_CATALOGUE_BARCODE_REPLACE_QUERY = exports.VarusCatalogueTaxGroup = exports.STORE_CATALOGUE_WEIGHT_PREFIX_QUERY = exports.VarusCatalogueWeightPrefix = exports.VarusCatalogueForbiddenBarcode = exports.VarusCatalogueBarcode = exports.VarusCatalogueArticle = exports.VarusCatalogueWaitingReason = exports.VarusCatalogueState = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
const sync_js_1 = require("./sync.js");
/**
 * Varus catalogue projection (VL-134) — the terminal-facing wire shape of a
 * Masterkasa store catalogue imported by VL-74.
 *
 * This is a **whole-snapshot replacement feed**, not a delta, and that is the
 * single decision the rest of the contract hangs off. Every other download
 * domain in `sync.ts` ships an `(updatedAt, id)` delta with `delete`
 * tombstones, because those domains are BOS-authored rows that change one at a
 * time. A Masterkasa catalogue is not: it is staged wholesale into a
 * generation and swapped in atomically, and half of one generation merged over
 * half of another is a till selling a price that was never on any price list.
 *
 * So there are no `delete` records here. A page belongs to a `version`; the
 * terminal replaces its whole Varus catalogue for that version, and rows the
 * new version does not contain are gone by construction. If the version moves
 * while a terminal is walking it, the server says `restartRequired` and the
 * terminal starts the replacement again rather than committing a mixture.
 *
 * `state` is the readiness contract. `waiting` means the terminal MUST NOT sell
 * from this catalogue — it maps onto `VarusScanWaitingForFeeds` in
 * `reform_lite_local_store`, which refuses scans honestly instead of falling
 * back to the shared catalogue and resolving the same barcode under different
 * customer rules.
 */
/** Whether the calling terminal's store may be sold from. */
exports.VarusCatalogueState = zod_1.z.enum(['ready', 'waiting']);
/**
 * Why a terminal is waiting. Operator-facing causes only — never a technical
 * error, for the reason `masterkasa-status.ts` gives: a cashier cannot act on a
 * SQL error, and a message they cannot act on teaches them to ignore the next.
 */
exports.VarusCatalogueWaitingReason = zod_1.z.enum([
    /** The calling terminal is not assigned to a store. */
    'terminal-unassigned',
    /** The store has no enabled Masterkasa source configured. */
    'source-unconfigured',
    /** No full import has ever completed for this store. */
    'never-imported',
    /** A newer generation is imported but not yet validated and published. */
    'snapshot-replacing',
    /** A published snapshot was withdrawn; nothing may be served until republished. */
    'snapshot-invalidated',
]);
/**
 * A product card — `t_articles`, projected onto the terminal's `varus_articles`.
 *
 * `taxGroup` is the INTEGER `t_articles.taxgroup`, not the fiscal group letter
 * (customer decision, 2026-09-12). `VarusScanLine.taxGroup` is already an int
 * with a fail-closed int→String resolver on the till, and the letter is a
 * property of the fiscal device's tax table, not of the product card.
 *
 * `ageRestriction` rides HERE, in the product payload, rather than on a
 * separate feed (same decision). The till's `VarusScanBinding` refuses a line
 * whose age restriction is unknown, so an age that arrives on its own schedule
 * would make every scan fail for as long as the two feeds disagreed. `0` means
 * unrestricted; the field is never optional, because "absent" and "not
 * restricted" must not be the same value on the wire.
 */
exports.VarusCatalogueArticle = zod_1.z.object({
    /** `t_articles.article` — product identity (VL-74 BR-8). */
    article: zod_1.z.string().min(1),
    /** `t_articles.fullname`. */
    fullName: zod_1.z.string().min(1),
    /** `t_articles.shortname` — the preferred receipt-line name (VL-75 BR-14). */
    shortName: zod_1.z.string(),
    /** `t_articles.taxgroup`, verbatim. Resolve to a category via `taxGroups`. */
    taxGroup: zod_1.z.number().int(),
    /** Minimum customer age. `0` = unrestricted. */
    ageRestriction: zod_1.z.number().int().min(0).max(120),
    /**
     * How many excise stamps (акцизні марки) ONE unit of the product carries —
     * `t_articles.is_marked` / `t_articles.count_marks` (VL-113, VL-339). `0`
     * means the product is sold without a stamp, whatever its tax category:
     * excise TAX does not imply an excise STAMP (beer carries excise and no
     * stamp). The till asks for this many stamps per unit.
     *
     * Optional, and absent is NOT `0`: a producer built before VL-339, or a
     * snapshot staged before the store's `is_marked` was imported, cannot say,
     * and a till then keeps its older rule (a stamp for every line in an excise
     * tax category). Absent therefore asks for more stamps, never fewer.
     */
    exciseMarks: zod_1.z.number().int().min(0).max(50).optional(),
    /**
     * The product's UKTZED code (код УКТ ЗЕД) — `t_articles.uktved` (VL-409,
     * VL-112). A till puts it in the product row of its PRRO document
     * (`<UKTZED>` in the ДПС check XML) for every product that carries one;
     * the tax authority checks it on excise goods.
     *
     * 4-10 ASCII digits. Optional, and absent means "no code": a producer
     * built before VL-409, a store row without one (most products), or a
     * value the producer refused as malformed. A till then sends the row
     * without the tag, exactly as before.
     */
    uktzed: zod_1.z
        .string()
        .regex(/^[0-9]{4,10}$/)
        .optional(),
    /**
     * The product's group — `t_articles.article_group` (VL-394). The store
     * identifies State Lottery tickets by group 156 (Bohdan, 2026-10-23): they
     * share tax group 5 with other goods, but are fiscalised at 0% (VL-439).
     *
     * Optional, and absent is NOT "no group": a producer built before VL-394, or
     * a store row without a group, cannot say, and a till must not read that as
     * "not a lottery ticket".
     */
    articleGroup: zod_1.z.number().int().min(0).max(2_147_483_647).optional(),
});
/**
 * A barcode row — `t_bars`, projected onto `varus_barcodes` AND
 * `product_fiscal_meta`.
 *
 * One record, two terminal tables, on purpose. `product_fiscal_meta` (VL-52 /
 * VL-53) needs the receipt barcode, the Masterkasa article id, the raw
 * `back_info` and the `dtype = 2` main-barcode marker — every one of which is a
 * field of the same `t_bars` row this record already carries. Emitting a second
 * record per barcode would double the wire volume of the largest family in the
 * feed to repeat data the terminal already has in front of it.
 *
 * Price-embedded and markdown (уцінка) barcodes are ordinary rows here: they
 * exist verbatim in `t_bars.bar` and are projected verbatim, which is what lets
 * the till resolve them by exact match before it ever tries weighted parsing
 * (VL-75 BR-3/BR-4).
 */
exports.VarusCatalogueBarcode = zod_1.z.object({
    /** `t_bars.bar`, verbatim — including price-embedded and markdown codes. */
    bar: zod_1.z.string().min(1),
    /**
     * Price-list discriminator from `back_info`; `0` is the main list, and only
     * main-list rows are sellable through the scan window (VL-75 BR-17).
     */
    priceListId: zod_1.z.number().int().min(0),
    /** `t_bars.article` — the owning `VarusCatalogueArticle.article`. */
    article: zod_1.z.string().min(1),
    /** `t_bars.price` converted to integer minor units (kopecks), as a string. */
    priceMinor: pricing_js_1.MoneyMinor,
    /** ISO-4217 currency of `priceMinor`. */
    currency: zod_1.z.string().length(3),
    /**
     * `t_bars.measure`: 0 = piece, 1 = weight (kg). Any other value fails the
     * scan. On a scale label it also says what the five value digits are: a
     * piece count under 0, grams under 1 — whichever prefix matched (VL-334).
     */
    measure: zod_1.z.number().int(),
    /** `t_bars.dtype`. `2` marks the card's main barcode (VL-52). */
    dtype: zod_1.z.number().int().nullable(),
    /** `t_bars.amount_default` as integer thousandths; `1000` = 1 piece / 1.000 kg. */
    amountDefaultThousandths: zod_1.z.number().int(),
    /** Raw `t_bars.back_info`, preserved for receipt replication (VL-75 BR-18). */
    backInfo: zod_1.z.string().nullable(),
    /**
     * `t_bars.old_price` — the price before the store's reduction — in integer
     * minor units, as a string (VL-49).
     *
     * Present ONLY when it is above [priceMinor]: the store's rule is that a
     * till sends `old_price − price` to the CRM as the line's CashdeskDiscount
     * (× quantity, or × weight) and strikes the old price on screen, and that
     * an equal, lower or absent old price is neither sent nor stored. Absent
     * also from a producer built before VL-49, and then a till rings the
     * barcode exactly as before. Never printed on the receipt.
     */
    oldPriceMinor: pricing_js_1.MoneyMinor.optional(),
    /**
     * `t_bars.ishot` — the barcode's position in the till's «Гарячі товари»
     * (hot items) window, 1 first (VL-428).
     *
     * Present ONLY on a hot barcode (`ishot > 0`); absent on an ordinary one,
     * and from a producer built before VL-428. Each barcode is its own row in
     * the window, even two of one article. A till writes the absence as "not
     * hot", so a barcode whose `ishot` went back to 0 leaves the window with
     * the next snapshot.
     */
    hotPosition: zod_1.z.number().int().positive().optional(),
    /**
     * `t_bars.id`, the store's own row id — the tie-break between two barcodes
     * on one [hotPosition], lower first (VL-428). Sent only with
     * [hotPosition]; a hot barcode whose row id the producer did not stage
     * comes without it, and the till then breaks the tie by barcode.
     */
    sourceRowId: zod_1.z.number().int().optional(),
});
/** A barcode prohibited for sale — `t_forbidden_bars` (VL-75 BR-19). */
exports.VarusCatalogueForbiddenBarcode = zod_1.z.object({
    bar: zod_1.z.string().min(1),
});
/**
 * A store-configured weighted-barcode prefix — `t_params` (VL-75 BR-7, VL-334).
 *
 * `prefix` is 2 digits (`21`) or, since VL-334, 3 digits (`250`, `271`); a
 * till matches the longest configured prefix a scanned code starts with. The
 * PLU stays the code's first 7 digits either way (Masterkasa pairs a 3-digit
 * prefix with a 4-digit item code).
 *
 * **A 3-digit prefix is served only to a till that asks for it** with
 * `?weightPrefixes=3` ({@link STORE_CATALOGUE_WEIGHT_PREFIX_QUERY}): a till
 * without VL-334 stores the prefix in a two-character column and throws on a
 * longer one, which would stall its whole catalogue walk. Old tills keep
 * receiving exactly the 2-digit prefixes.
 *
 * The prefix only marks a code as a scale label and fixes where its item code
 * ends. It never says what the five value digits after the PLU are: that is
 * the resolved barcode's own `measure` (`0` a piece count, `1` grams), as the
 * store stated on 2026-09-29. `t_params.divider_weight_<prefix>` is therefore
 * not carried.
 */
exports.VarusCatalogueWeightPrefix = zod_1.z.object({
    prefix: zod_1.z.string().regex(/^\d{2,3}$/, 'a two- or three-digit EAN-13 weighted prefix'),
});
/**
 * The query parameter a till sends on `GET /sync/store-catalogue` to say it
 * reads 3-digit weighted prefixes (VL-334); the value is the longest prefix
 * length it understands. Absent, or any value but `3`, and the page carries
 * 2-digit prefixes only.
 */
exports.STORE_CATALOGUE_WEIGHT_PREFIX_QUERY = 'weightPrefixes';
/**
 * The per-product tax mapping the till resolves a basket line through (VL-106).
 *
 * Keyed on the INTEGER tax group, matching `VarusCatalogueArticle.taxGroup` and
 * the till's `VarusTaxCategoryResolver`. `taxCategory` is the country-pack tax
 * category key — the same vocabulary `sync.tax-rates` and `CatalogBootstrapItem`
 * use — so a Varus line reaches the same tax table as every other line.
 *
 * VL-136's `tax_rules.tax_groups` on `sync.terminal-config` keys by `code`, the
 * Cyrillic fiscal group LETTER the fiscal device knows. The two are different
 * keys for different jobs and are reconciled by position in the pipeline: this
 * feed takes a basket line from `t_articles.taxgroup` to a tax category, and
 * VL-136 takes the resulting fiscal department to a rate on the device.
 */
exports.VarusCatalogueTaxGroup = zod_1.z.object({
    /** `t_articles.taxgroup`. */
    taxGroup: zod_1.z.number().int(),
    /** Country-pack tax category key. */
    taxCategory: zod_1.z.string().min(1),
});
/**
 * The query parameter a till sends on `GET /sync/store-catalogue` to say it
 * stores `barcode-replace-rule` records (VL-341); the value is `1`.
 *
 * A till built before VL-341 throws on a record kind it does not know, which
 * would stall its whole catalogue walk — so the rules are served only to a
 * till that asks, exactly as 3-digit weighted prefixes are (VL-334). An older
 * till simply never applies the mechanic and transmits the scanned code.
 */
exports.STORE_CATALOGUE_BARCODE_REPLACE_QUERY = 'barcodeReplace';
/**
 * One `[BarcodeReplace]` mechanic row — a `BarcodeReplace` object in
 * `t_back_table1` with its `t_back_table2` properties (VL-53, VL-341),
 * projected onto the till's `barcode_replace_rules` +
 * `barcode_replace_products`.
 *
 * The mechanic rewrites only what a till sends to the fiscal register (print
 * and tax service): the leftmost `leftReplacePosition` digits of a listed
 * product's EAN-13, `oldBar` → `newBar`, with the check digit recomputed. It
 * changes no price and no lookup — the scanned code still resolves the item,
 * and the till's own records keep it.
 *
 * Projected by `projectBarcodeReplaceRule` in `@reform-lite/connectors`, which
 * documents what was read off a live store: the article list arrives split
 * over `ProdID1..N` in 255-character pieces, so it is ONE list here
 * (`listIndex` 1).
 */
exports.VarusCatalogueBarcodeReplaceRule = zod_1.z.object({
    /** The object's `t_back_table1.uid`, as text. */
    id: zod_1.z.string().min(1),
    /** Evaluation order; the first matching rule wins, lowest first. */
    priority: zod_1.z.number().int(),
    /** `OldBar` — the digits currently at the barcode's left edge. */
    oldBar: zod_1.z.string().regex(/^\d+$/),
    /** `NewBar` — the digits that replace them. */
    newBar: zod_1.z.string().regex(/^\d+$/),
    /** `LeftReplacePosition` — how many leftmost digits are replaced. */
    leftReplacePosition: zod_1.z.number().int().min(1).max(12),
    /**
     * True = a product must be in EVERY non-empty `listIndex` list, false = in
     * any. The projection sends one list, so it sends false.
     */
    isClassifierAnd: zod_1.z.boolean(),
    /** `PaysType` codes (0=cash, 1=card, 2=cheque, 3=credit). Empty = any tender. */
    paysTypes: zod_1.z.array(zod_1.z.number().int().min(0)),
    /**
     * `BDate`: the first STORE-LOCAL calendar day the rule applies, `YYYY-MM-DD`.
     * A day rather than an instant because the source holds days and the till
     * reads them on its own wall clock, which is the store's. Null = unbounded.
     */
    beginsOn: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /** `EDate`: the last store-local calendar day, inclusive. Null = unbounded. */
    endsOn: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /** Article membership; `listIndex` is 1..5 (`ProdID1..ProdID5` on the till). */
    products: zod_1.z.array(zod_1.z.object({
        listIndex: zod_1.z.number().int().min(1).max(5),
        productId: zod_1.z.string().min(1),
    })),
});
/**
 * The query parameter a till sends on `GET /sync/store-catalogue` to say it
 * stores the store's card-programme tables (RL-830); the value is `1`.
 *
 * Same reason as {@link STORE_CATALOGUE_BARCODE_REPLACE_QUERY}: a till that
 * does not know the record kinds below throws on them and stalls its walk, so
 * they are served only to a till that asks.
 */
exports.STORE_CATALOGUE_CARD_PROGRAMMES_QUERY = 'cardProgrammes';
/**
 * The card-programme datasets a page set can carry (RL-830). Each is a whole
 * table on the till, replaced in full when a walk that announced it completes.
 *
 * - `back-table-objects`: payment-rule objects of `t_back_table1` with their
 *   `t_back_table2` properties (today the `CashBack` object).
 * - `pwl-barcodes`: `t_cashback_pwl`, the goods a programme card may pay for.
 * - `card-prices`: `t_perfect`, the per-article price a loyalty-card holder
 *   is quoted (information for the cashier; the CRM decides the discount).
 */
exports.StoreCatalogueProgrammeDataset = zod_1.z.enum([
    'back-table-objects',
    'pwl-barcodes',
    'card-prices',
]);
/**
 * Leads the first page of a walk served to a till that asked for the
 * card-programme tables (RL-830). It is what lets the till tell "the store's
 * table is empty" (announced, no rows → the till's table becomes empty) from
 * "this server does not deliver it" (not announced → the till keeps what it
 * has and still knows it never received one).
 */
exports.StoreCatalogueProgrammeTables = zod_1.z.object({
    datasets: zod_1.z.array(exports.StoreCatalogueProgrammeDataset),
});
/**
 * One payment-rule object of the store's back tables (RL-830), copied
 * unchanged: `t_back_table1(uid, name, note)` and every `t_back_table2(key,
 * value)` row on the same `uid`, in source order. The till parses the values
 * (validity dates `dd.mm.yyyy`, 9-digit card prefixes, messages); nothing is
 * trimmed, converted or dropped here.
 */
exports.StoreCatalogueBackTableObject = zod_1.z.object({
    uid: zod_1.z.number().int(),
    name: zod_1.z.string().min(1),
    note: zod_1.z.string().nullable(),
    properties: zod_1.z.array(zod_1.z.object({ key: zod_1.z.string().min(1), value: zod_1.z.string() })),
});
/** One `t_cashback_pwl.bar` (RL-830), copied unchanged. */
exports.StoreCataloguePwlBarcode = zod_1.z.object({
    bar: zod_1.z.string().min(1),
});
/**
 * One `t_perfect` row (RL-830): the unit price a loyalty-card holder is
 * quoted for `article`, in minor units as a decimal string (like a barcode's
 * `priceMinor`). Only `article` and `perfect_price` travel. Per piece, or per
 * kilogram for goods sold by weight, as `t_bars.price` is.
 */
exports.StoreCatalogueCardPrice = zod_1.z.object({
    article: zod_1.z.string().min(1),
    perfectPriceMinor: pricing_js_1.MoneyMinor,
    currency: zod_1.z.string().length(3),
});
/**
 * One row of the snapshot.
 *
 * The entity is on the discriminator, as `SyncCatalogDeltaItem` does it, because
 * the payloads share no field that could carry it. There is no `delete` kind —
 * see the file header.
 */
exports.VarusCatalogueRecord = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('article'), id: zod_1.z.string(), payload: exports.VarusCatalogueArticle }),
    zod_1.z.object({ kind: zod_1.z.literal('barcode'), id: zod_1.z.string(), payload: exports.VarusCatalogueBarcode }),
    zod_1.z.object({
        kind: zod_1.z.literal('forbidden-barcode'),
        id: zod_1.z.string(),
        payload: exports.VarusCatalogueForbiddenBarcode,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('weight-prefix'),
        id: zod_1.z.string(),
        payload: exports.VarusCatalogueWeightPrefix,
    }),
    zod_1.z.object({ kind: zod_1.z.literal('tax-group'), id: zod_1.z.string(), payload: exports.VarusCatalogueTaxGroup }),
    zod_1.z.object({
        kind: zod_1.z.literal('barcode-replace-rule'),
        id: zod_1.z.string(),
        payload: exports.VarusCatalogueBarcodeReplaceRule,
    }),
    // RL-830: served only with `?cardProgrammes=1`.
    zod_1.z.object({
        kind: zod_1.z.literal('programme-tables'),
        id: zod_1.z.string(),
        payload: exports.StoreCatalogueProgrammeTables,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('back-table-object'),
        id: zod_1.z.string(),
        payload: exports.StoreCatalogueBackTableObject,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('pwl-barcode'),
        id: zod_1.z.string(),
        payload: exports.StoreCataloguePwlBarcode,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('card-price'),
        id: zod_1.z.string(),
        payload: exports.StoreCatalogueCardPrice,
    }),
]);
/**
 * `GET /sync/store-catalogue`, and its deprecated alias `GET
 * /sync/varus-catalogue` (RL-466) — one handler, identical pages.
 *
 * A `waiting` response carries no rows and no version. A `ready` response
 * carries a `version` that is stable for the whole walk: the terminal commits
 * the replacement only after the last page of ONE version.
 */
exports.SyncVarusCatalogueResponse = zod_1.z.object({
    state: exports.VarusCatalogueState,
    /** Null when `state` is `ready`. */
    reason: exports.VarusCatalogueWaitingReason.nullable(),
    /**
     * The completion marker's wire version, as a base-10 string. It combines
     * the publication counter and register scope, so correcting a terminal's
     * register number reloads its prohibition rows without a source-data change.
     * JSON numbers cannot safely carry this value. Null while waiting.
     */
    version: zod_1.z.string().regex(/^\d+$/, 'a base-10 catalogue version').nullable(),
    /** The store this catalogue belongs to. Null when the till has no store. */
    locationId: zod_1.z.string().nullable(),
    items: zod_1.z.array(exports.VarusCatalogueRecord),
    cursor: sync_js_1.SyncCursor,
    hasMore: zod_1.z.boolean(),
    /**
     * The version moved under a walk in progress. The terminal MUST discard what
     * it has staged and restart from a cursor-less pull; it must never commit a
     * catalogue assembled from two versions.
     */
    restartRequired: zod_1.z.boolean(),
    snapshotAt: zod_1.z.string().datetime(),
});
/**
 * The neutral name for the same wire shape (RL-466). New code uses this one;
 * the `Varus*` schema names are internal and stay until the projection itself
 * is renamed.
 */
exports.SyncStoreCatalogueResponse = exports.SyncVarusCatalogueResponse;
//# sourceMappingURL=varus-catalogue.js.map